/**
 * Hook React pour charger les valeurs du CMS pour une liste de keys donnée.
 *
 * Usage dans une page publique :
 *   const c = useSiteContent(["home.hero.title", "home.hero.subtitle"]);
 *   return <h1>{c("home.hero.title", "Titre par défaut")}</h1>;
 *
 * Le hook charge une seule fois au mount, cache par stringified keys.
 * Fallback systématique sur la valeur hardcodée si la key n'existe pas en DB.
 */
import { useEffect, useState, useCallback } from "react";
import api, { assetUrl } from "@/lib/api";

// Cache module-level pour éviter de re-fetch entre composants
const _cache = new Map(); // key → value
const _pending = new Map(); // requestKey → Promise

export function useSiteContent(keys = []) {
    const requestKey = keys.slice().sort().join(",");
    const [version, setVersion] = useState(0);

    useEffect(() => {
        if (!requestKey) return;
        // Check si tout est déjà en cache
        if (keys.every((k) => _cache.has(k))) return;
        // Sinon, fetch — dédupe via _pending
        const existing = _pending.get(requestKey);
        const p = existing || api.get(`/content?keys=${encodeURIComponent(requestKey)}`).then(({ data }) => {
            for (const k of keys) {
                _cache.set(k, data[k] ?? null);
            }
            _pending.delete(requestKey);
            return data;
        }).catch(() => {
            _pending.delete(requestKey);
            // Marque les keys comme "tentées mais vides" pour ne pas re-tenter en boucle
            for (const k of keys) _cache.set(k, null);
        });
        _pending.set(requestKey, p);
        p.finally(() => setVersion((v) => v + 1));
    }, [requestKey]); // eslint-disable-line react-hooks/exhaustive-deps

    // Getter : valeur DB si non-vide, sinon fallback.
    // Si la valeur est un asset backend (/api/...), on la résout en URL absolue.
    const get = useCallback((key, fallback = "") => {
        const v = _cache.get(key);
        const raw = (v === undefined || v === null || v === "") ? fallback : v;
        return typeof raw === "string" ? assetUrl(raw) : raw;
    }, [version]); // eslint-disable-line react-hooks/exhaustive-deps

    return get;
}


/** Invalide tout le cache (à appeler après un save admin). */
export function invalidateContentCache() {
    _cache.clear();
    _pending.clear();
}
